import React, { useContext } from "react";

import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import { readableColor } from "polished";
import PropTypes from "prop-types";
import styled, { ThemeContext } from "styled-components";

import { useStores } from "../../../../contexts/StoreContext";
import Icon from "../../../Icon/Icon";
import useDevice from "../../../customHooks/useDevice";
import Loader from "../../Loader/Loader";

const StyledLabel = styled.label`
  min-width: 0;
`;

const StyledContainer = styled.div`
  transition: max-height 0.5s ease-in-out, opacity 0.5s ease-in-out;
  max-height: ${(props) => (props.isHidden ? "0" : "1000px")};
  overflow: hidden;
`;

const StyledHeader = styled.div`
  padding: 6px 15px;
  margin-bottom: 15px;
  border-top: 1px solid #ccc;
  border-bottom: 1px solid #ccc;

  h5 {
    margin-bottom: 0px;
  }
  svg {
    cursor: pointer;
  }
`;

const StyledFilterWrapper = styled.div`
  background-color: ${({ isMobile, theme }) =>
    isMobile
      ? theme.primaryBackgroundColor
      : readableColor(
          theme.primaryBackgroundColor,
          theme.textColor,
          theme.negativeTextColor,
          false
        )};
  color: ${({ isMobile, theme }) =>
    isMobile
      ? readableColor(
          theme.primaryBackgroundColor,
          theme.textColor,
          theme.negativeTextColor,
          false
        )
      : theme.primaryBackgroundColor};
`;

const StyledCheckbox = styled.div`
  display: flex;
  align-items: center;

  input[type="checkbox"] {
    min-width: 16px;
    width: 16px;
    height: 16px;
    margin-right: 8px;
    appearance: none;
    outline: none;
    border: 1px solid
      ${({ isMobile, theme }) =>
        isMobile
          ? readableColor(
              theme.primaryBackgroundColor,
              theme.textColor,
              theme.negativeTextColor,
              false
            )
          : theme.primaryBackgroundColor};
    border-radius: 2px;
    cursor: pointer;
  }

  input[type="checkbox"]:checked {
    background-color: ${({ isMobile, theme }) =>
      isMobile
        ? readableColor(
            theme.primaryBackgroundColor,
            theme.textColor,
            theme.negativeTextColor,
            false
          )
        : theme.primaryBackgroundColor};
    border: 1px solid
      ${({ isMobile, theme }) =>
        isMobile
          ? readableColor(
              theme.primaryBackgroundColor,
              theme.textColor,
              theme.negativeTextColor,
              false
            )
          : theme.primaryBackgroundColor};
    position: relative;
  }

  input[type="checkbox"]:checked::after {
    content: "";
    width: 10px;
    height: 10px;
    background-color: ${({ isMobile, theme }) =>
      isMobile
        ? readableColor(
            theme.primaryBackgroundColor,
            theme.textColor,
            theme.negativeTextColor,
            false
          )
        : theme.primaryBackgroundColor};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 2px;
  }
`;

function FrontendFilter({ isLoading, type, label }) {
  const { isSmallScreen, isMobile } = useDevice();
  const { filtersStore } = useStores();
  const theme = useContext(ThemeContext);

  const {
    addFromFilter,
    removeFromFilter,
    searchResults,
    searchBuckets,
    frontendFilters,
  } = filtersStore;
  const [hide, setHide] = React.useState(false);

  const vacancyCount =
    searchBuckets[type]?.reduce((acc, { name, count }) => {
      acc[name] = count;
      return acc;
    }, {}) || {};
  const handleChange = (e, vacancyCategory) => {
    const isSelected = e.target.checked;

    if (isSelected) {
      addFromFilter(type, vacancyCategory);
    } else {
      removeFromFilter(type, vacancyCategory);
    }
  };

  return (
    <StyledFilterWrapper className="p-3" isMobile={isMobile || isSmallScreen}>
      <StyledHeader className="d-flex justify-content-between">
        <h5>{label}</h5>
        <div
          onClick={() => {
            setHide(!hide);
          }}
        >
          <Icon
            color={
              isMobile || isSmallScreen
                ? readableColor(
                    theme.primaryBackgroundColor,
                    theme.textColor,
                    theme.negativeTextColor,
                    false
                  )
                : theme.primaryBackgroundColor
            }
            icon={hide ? "chevronUp" : "chevronDown"}
            size={16}
          />
        </div>
      </StyledHeader>
      <StyledContainer isHidden={hide}>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {isEmpty(searchResults) && isEmpty(vacancyCount) && (
              <span>Nothing to filter</span>
            )}
            {Object.entries(vacancyCount).map(([name, count]) => {
              const isInputUsed =
                frontendFilters[type].includes(name) || vacancyCount[name] > 0;

              if (!isInputUsed) {
                return null;
              }

              return (
                <StyledCheckbox
                  key={name}
                  className="d-flex align-items-center my-1"
                  isMobile={isMobile || isSmallScreen}
                >
                  <input
                    checked={frontendFilters[type].includes(name)}
                    id={name}
                    onChange={(e) => handleChange(e, name)}
                    type="checkbox"
                  />
                  <StyledLabel
                    className="ms-2 d-flex flex-grow-1"
                    htmlFor={name}
                  >
                    <span className="text-truncate me-2">{name}</span>
                    <span className="ms-auto">{count || 0}</span>
                  </StyledLabel>
                </StyledCheckbox>
              );
            })}
          </>
        )}
      </StyledContainer>
    </StyledFilterWrapper>
  );
}
export default observer(FrontendFilter);

FrontendFilter.propTypes = {
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  type: PropTypes.string,
};

FrontendFilter.defaultProps = {
  isLoading: false,
  label: "Country",
  type: "country",
};
